/**
 * Оболочка комнаты сессии без разметки: фаза, остаток времени, превью последнего сообщения,
 * состав кебаба и слот голоса.
 *
 * Всё это решения, а не вёрстка, и живут они отдельно ровно потому, что одни и те же ответы
 * нужны двум поверхностям сразу – пилюле и окну. Пока такие ветки писались тернарниками прямо в
 * разметке, каждая поверхность считала «сколько осталось» и «что показывать вместо ленты» чуть
 * иначе, и человек, свернувший окно в пилюлю, видел другое состояние той же сессии.
 */

import { useEffect, useState } from 'react';

import type { VoiceRoomState } from '#modules/finder/features/posts/components/Post/VoiceRoomBlock';
import { isArchivedSessionStatus } from '#modules/finder/features/posts/model/sessionStatus';
import { serverNow } from '#features/date/utils/serverClock';

/**
 * Что комната показывает про саму сессию: сессия скоро, сессия идёт, сессии больше нет.
 *
 * Отмена отличается от обычного завершения словами, но обе фазы закрыты: составом и настройками
 * управлять уже нельзя, чат остаётся. Исключение участника отдельной фазой не становится: сессия
 * для остальных продолжается, поэтому его виджет просто исчезает.
 */
export type SessionRoomPhase = 'upcoming' | 'live' | 'ended' | 'cancelled';

type PhaseInput = {
  /** UPCOMING | LIVE | COMPLETED | CANCELLED. */
  status?: string | null;
  startedAt?: string | null;
  expiredAt?: string | null;
  /** Время снаружи: фаза целиком про «сейчас», иначе её нельзя ни проверить, ни пересчитать. */
  now: number;
};

const timeOf = (iso: string | null | undefined): number =>
  iso ? new Date(iso).getTime() : Number.NaN;

/**
 * Фаза комнаты.
 *
 * Порядок веток – это и есть правило. Сначала то, что случилось с сессией, и только потом часы:
 * ни отмена, ни завершение не двигают `expiredAt`, поэтому у закрытой сессии время часто ещё
 * идёт – и комната, судящая по одним часам, рисовала бы ей LIVE с зелёным прогрессом.
 *
 * Закрытая строка любого рода – это «сессии больше нет». Отмену сохраняем отдельной фазой,
 * потому что прощание и системная строка чата называют её прямо.
 */
export const resolveRoomPhase = ({
  status,
  startedAt,
  expiredAt,
  now,
}: PhaseInput): SessionRoomPhase => {
  if (status === 'CANCELLED') {
    return 'cancelled';
  }
  if (isArchivedSessionStatus(status)) {
    return 'ended';
  }

  const end = timeOf(expiredAt);
  if (Number.isFinite(end) && now >= end) {
    return 'ended';
  }

  // Play Now is already live on the server; this device may be seconds behind its clock.
  if (status === 'LIVE') {
    return 'live';
  }

  const start = timeOf(startedAt);
  if (Number.isFinite(start) && now < start) {
    return 'upcoming';
  }

  return 'live';
};

/**
 * Сессии уже нет: голоса нет, вместо яруса – прощание, внизу – кнопка «Закрыть».
 *
 * Вопрос задают четыре поверхности сразу, и спрашивают они именно это, а не «какая фаза»:
 * равенство с `'ended'`, размноженное по четырём файлам, разъезжалось бы при первом же изменении
 * набора фаз – ровно так и вышло с отменой, которую пришлось выпалывать из каждой ветки.
 */
export const isClosedPhase = (phase: SessionRoomPhase): boolean =>
  phase === 'ended' || phase === 'cancelled';

/**
 * Голос виден у живой сессии, а после завершения - пока комната открыта или я подключён.
 *
 * До 23.09.2026 ярус голоса пропадал ровно в конце сессии, а соединение жило дальше: меня было
 * слышно, а микрофон и выход исчезали с экрана. Теперь сервер держит голос, пока в нём говорят,
 * и комната держит органы голоса ровно столько же.
 */
export const showsVoiceTier = (
  phase: SessionRoomPhase,
  inThisRoom: boolean,
  state?: VoiceRoomState,
  roomStatus?: string | null
): boolean =>
  !isClosedPhase(phase) ||
  inThisRoom ||
  roomStatus === 'OPEN' ||
  (state === 'connectionLost' && roomStatus !== 'CLOSED');

/** Шаг кнопки «Продлить». Тот же, что держит сервер: он и решает, клиент только просит. */
export const EXTEND_STEP_MS = 30 * 60 * 1000;

/** Потолок длины сессии от старта. Сервер держит тот же и отказывает сам. */
export const SESSION_MAX_LENGTH_MS = 8 * 60 * 60 * 1000;

/**
 * Можно ли ещё продлить: сессия не закончилась и до потолка есть место.
 *
 * Отвечает только за то, показывать ли пункт. Права (автор) и пересечения у команды проверяет
 * сервер: пункт у сессии, которую он всё равно не продлит, лучше честного отказа не был бы.
 */
export const canExtendSession = (
  startedAt: string | null | undefined,
  expiredAt: string | null | undefined,
  now: number
): boolean => {
  const end = timeOf(expiredAt);
  if (!Number.isFinite(end) || end <= now) {
    return false;
  }
  const start = timeOf(startedAt);
  if (!Number.isFinite(start)) {
    return true;
  }
  return end < start + SESSION_MAX_LENGTH_MS;
};

/** Сколько осталось играть. `null` – в фазах, где остатка не бывает. */
export type RoomRemaining = { kind: 'minutes'; minutes: number } | { kind: 'lessThanMinute' };

/**
 * Остаток – в минутах и без секунд.
 *
 * Секунды здесь были бы отсчётом до конца игры, которого никто не ждёт: сессия не экзамен.
 * Округляем вверх, потому что «1 мин» на 30 секундах честнее, чем «0 мин»; последняя минута
 * говорит словами.
 */
export const resolveRemaining = (
  phase: SessionRoomPhase,
  expiredAt: string | null | undefined,
  now: number
): RoomRemaining | null => {
  if (phase !== 'live') {
    return null;
  }

  const end = timeOf(expiredAt);
  if (!Number.isFinite(end)) {
    return null;
  }

  const left = end - now;
  if (left <= 0) {
    return null;
  }
  if (left < 60_000) {
    return { kind: 'lessThanMinute' };
  }

  return { kind: 'minutes', minutes: Math.ceil(left / 60_000) };
};

/**
 * «09:59» до старта. mm:ss – единственное место, где комната показывает секунды: до старта они
 * и есть содержание ожидания, а окно показа всего десять минут.
 *
 * Больше часа не бывает по правилу показа, но если часы разъехались – показываем потолок, а не
 * трёхзначные минуты, которые сломают ширину чипа.
 */
export const formatStartsIn = (startedAt: string | null | undefined, now: number): string => {
  const start = timeOf(startedAt);
  const left = Number.isFinite(start) ? Math.max(0, start - now) : 0;
  const total = Math.min(Math.ceil(left / 1000), 59 * 60 + 59);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/** Что стоит второй строкой пилюли. */
export type RoomPreview =
  /** Чата ещё нет – его заводит другой сервис по событию. */
  | { kind: 'creating' }
  | { kind: 'deleted' }
  | { kind: 'empty' }
  /** «Ник: текст». Имени может не быть – тогда одна реплика. */
  | { kind: 'message'; name: string | null; text: string };

type PreviewInput = {
  chatId?: string | null;
  isChatDeleted?: boolean;
  lastMessageText?: string | null;
  /**
   * Автор последней реплики. Запрос комнаты его не отдаёт вовсе, поэтому имя приходит снаружи и
   * только когда его кто-то знает: подставлять сюда чужое имя хуже, чем показать одну реплику.
   */
  lastMessageAuthor?: string | null;
};

export const resolvePreview = ({
  chatId,
  isChatDeleted,
  lastMessageText,
  lastMessageAuthor,
}: PreviewInput): RoomPreview => {
  if (!chatId) {
    return { kind: 'creating' };
  }
  if (isChatDeleted) {
    return { kind: 'deleted' };
  }

  const text = lastMessageText?.trim();
  if (!text) {
    return { kind: 'empty' };
  }

  const name = lastMessageAuthor?.trim();

  return { kind: 'message', name: name || null, text };
};

/**
 * Пункты кебаба. Порядок массива – порядок в меню.
 *
 * «Пригласить» здесь нет (12.09.2026): звать людей в сессию – дело страницы заявки и окна
 * сессии, где стоит панель ссылки. Меню комнаты – про то, что происходит со мной в этой сессии.
 */
export type RoomMenuItem =
  | 'openSession'
  | 'sessionSettings'
  | 'extend'
  | 'muteChat'
  | 'audioSettings'
  | 'leave';

type MenuInput = {
  phase: SessionRoomPhase;
  /**
   * Есть ли кому открыть список устройств.
   *
   * Открыть его может только владелец соединения LiveKit – ярус голоса; поверхность, которая
   * его не держит, пункту дать нечего. Пункт без обработчика стоял бы вечно серым и учил бы,
   * что «Настройки звука» в комнате не работают вовсе – а они работают, просто из яруса.
   */
  hasAudioSettings: boolean;
  /**
   * Смотрит автор сессии. Ему «Выйти из сессии» не показываем: сервер автору выйти не даёт и
   * отвечал безликим «Что-то пошло не так». Его действия – «Завершить» и «Отменить», они живут в
   * «Настройках сессии».
   */
  isHost: boolean;
  /** Переписка сессии заведена: только тогда можно заглушить её уведомления. */
  hasChat?: boolean;
  /** Сессию ещё можно продлить (`canExtendSession`). Пункт только у автора. */
  canExtend?: boolean;
};

/**
 * Состав меню.
 *
 * У закрытой сессии пунктов не остаётся: настраивать в ней нечего, а «Выйти из сессии», из
 * которой уже вышли все, – действие без последствий, притворяющееся опасным. Переписку после
 * завершения покидают из её собственной шапки.
 */
export const roomMenuItems = ({
  phase,
  hasAudioSettings,
  isHost,
  hasChat = false,
  canExtend = false,
}: MenuInput): RoomMenuItem[] => {
  /*
    Закончившаяся сессия: пунктов про саму сессию не остаётся ни одного (26.09.2026).

    Раньше здесь стояли «Открыть сессию» и «Выйти из чата» - действия над тем, чего уже нет, и
    оба вели в тупик. «Выйти из чата» после первого же нажатия отвечал «не найдено»: комната
    про удавшийся выход не узнавала и продолжала звать нажать ещё раз. А сама дверь из переписки
    никуда не делась - она стоит в шапке треда, где человек её и ищет, когда решает прибраться
    в списке чатов.

    Единственный пункт, который у закончившейся сессии может быть, - голосовой: разговор
    переживает сессию, и выйти из него надо откуда-то. Его дописывает `menuItemsWithVoiceExit`,
    и только когда человек действительно в голосе; пустой список означает, что кебаба у комнаты
    нет вовсе.
  */
  if (isClosedPhase(phase)) {
    return [];
  }

  return [
    'openSession',
    'sessionSettings',
    /*
      Продлить - сразу за настройками: это тоже решение автора о сессии, только частое и срочное,
      его жмут посреди игры, не открывая окна.
    */
    ...(isHost && canExtend ? (['extend'] as const) : []),
    ...(hasChat ? (['muteChat'] as const) : []),
    ...(hasAudioSettings ? (['audioSettings'] as const) : []),
    ...(isHost ? [] : (['leave'] as const)),
  ];
};

/**
 * Кого показывать лицами – одно правило на пилюлю и на полосу.
 *
 * Вопрос у обеих один и тот же: «кто сейчас в голосе». Отвечать на него разными списками –
 * значит показывать в углу экрана и над навигацией разных двух человек в одной и той же сессии;
 * человек, повернувший планшет, видел бы смену состава там, где ничего не менялось.
 *
 * Порядок состава сохраняется: смена говорящего меняет кольцо, а не положение лица.
 */
export const voiceFaces = <T extends { inVoice: boolean; speaking: boolean }>(
  members: readonly T[]
): T[] => members.filter((member) => member.inVoice);

/** Что стоит в правой колонке пилюли на месте голоса. */
export type PillVoiceSlot = 'mic' | 'join' | 'return' | 'discord' | 'none';

/**
 * Слот голоса мини-виджета: вход либо общая группа микрофона, устройств и выхода.
 *
 * Поэтому здесь только те состояния, где действие есть и оно однозначно: мой микрофон, вход в
 * открытую комнату и возврат после обрыва. Всё остальное – объяснения («голос в другой вкладке»,
 * «микрофон занят звонком», «откроется в 16:20»), а объяснению в пилюле места нет: его читают в
 * окне, в ярусе голоса, где под него отведена целая строка. Кнопка без подписи, зовущая в отказ,
 * хуже пустого места.
 */
export const resolvePillVoiceSlot = (
  state: VoiceRoomState,
  phase: SessionRoomPhase,
  roomStatus?: string | null
): PillVoiceSlot => {
  if (isClosedPhase(phase) && roomStatus !== 'OPEN') {
    /*
      Сессия кончилась, а я ещё в голосе (23.09.2026: голос больше не рвут вместе с сессией).
      Микрофон обязан остаться под рукой: слышно меня по-прежнему, и выключатель пропасть не
      может. Закрытая комната не даёт нового входа; при OPEN вход сохраняется и после сессии.
    */
    if (state === 'joined' || state === 'connecting' || state === 'reconnecting') {
      return 'mic';
    }
    // Связь оборвалась посреди разговора после конца сессии (или голос забрало другое
    // устройство): сервер пускает обратно, пока комната открыта, и кнопка возврата нужна здесь.
    return (state === 'connectionLost' || state === 'otherTab') && roomStatus !== 'CLOSED'
      ? 'return'
      : 'none';
  }

  switch (state) {
    case 'joined':
    case 'connecting':
    case 'reconnecting':
      return 'mic';
    case 'empty':
    case 'occupied':
      return 'join';
    case 'connectionLost':
      return 'return';
    /*
      Голос забрало другое устройство. Действие однозначное – «Вернуть сюда», и до 02.10.2026 его
      в полосе не было: на телефоне свёрнутая полоса просто молчала, и человек не понимал, почему
      его не слышно (прогон на телефоне 02.10.2026).
    */
    case 'otherTab':
      return 'return';
    /*
      У Discord-сессии действие тоже однозначное – уйти в канал гильдии, – и до 12.09.2026 слот
      у неё пустовал: комната знала только про свой голос. Пустое место при этом не означало
      «делать нечего», а означало «мы не придумали»: канал есть, ссылка на него приезжает вместе
      с комнатой.
    */
    case 'discord':
      return 'discord';
    case 'inGame':
      return 'none';
    default:
      return 'none';
  }
};

/**
 * Меньше этого сдвига – нажатие, а не перенос. То же число, что внутри `useCornerDrag`: пилюля
 * одновременно и цель нажатия, и ручка переноса, а жест там заканчивается обычным `click`, и без
 * этой проверки любое перетаскивание пилюли в соседний угол разворачивало бы её в окно.
 */
export const CLICK_SLOP_PX = 6;

export const movedBeyondClickSlop = (
  from: { x: number; y: number } | null,
  to: { x: number; y: number }
): boolean => from !== null && Math.abs(to.x - from.x) + Math.abs(to.y - from.y) >= CLICK_SLOP_PX;

/**
 * Слои, которые Escape закрывает раньше, чем окно свернётся.
 *
 * Всё это Ark UI (Chakra 3), и каждый такой слой сам гасит себя по Escape на уровне документа –
 * поповер устройств, кебаб комнаты, эмодзи-пикер композера, диалог подтверждения. Признак у них
 * общий: `data-state="open"` при своём `data-scope`. Ищем по всему документу, а не внутри окна:
 * все они уезжают в портал и в разметке окна их нет.
 *
 * Подсказок (`tooltip`) и раскрытого состава (`collapsible`) в списке нет намеренно: Escape для
 * них не действие человека, а случайность, и отдавать им нажатие значило бы, что окно перестаёт
 * сворачиваться, пока курсор стоит на кнопке.
 */
const DISMISSABLE_SCOPES = ['menu', 'popover', 'dialog', 'select', 'combobox', 'listbox'] as const;

const DISMISSABLE_LAYER_SELECTOR = DISMISSABLE_SCOPES.map(
  (scope) => `[data-scope="${scope}"][data-state="open"]`
).join(',');

/** Есть ли на экране слой, которому Escape нужнее, чем окну. */
export const hasOpenDismissableLayer = (doc: Document | null | undefined): boolean =>
  Boolean(doc?.querySelector(DISMISSABLE_LAYER_SELECTOR));

/** Поля, где Escape принадлежит вводу: там он выходит из набора, а не убирает окно. */
const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

export const isTextEntry = (node: EventTarget | null): node is HTMLElement => {
  if (typeof HTMLElement === 'undefined' || !(node instanceof HTMLElement)) {
    return false;
  }
  if (node.isContentEditable || node.tagName === 'TEXTAREA') {
    return true;
  }

  return node.tagName === 'INPUT' && !NON_TEXT_INPUT_TYPES.has((node as HTMLInputElement).type);
};

/** Что Escape делает в окне комнаты. */
export type EscapeAction =
  /** Нажатие уже чьё-то: его разбирает открытый слой, чужой обработчик или ввод иероглифов. */
  | 'ignore'
  /** Выйти из набора: первый Escape отпускает поле, и только следующий убирает окно. */
  | 'leaveField'
  | 'collapse';

/**
 * Escape в окне – это «закрой то, что сверху», а не «сверни комнату».
 *
 * Обработчик на коробке видит нажатие последним: сначала его разбирает всё, что внутри. Пока
 * отсеивался один `defaultPrevented`, Escape в поповере устройств закрывал поповер И сворачивал
 * окно – два действия на одно нажатие, причём второе человек не просил. Ark гасит свои слои
 * слушателем на документе и до `preventDefault` доходит не всегда, поэтому спрашиваем сам экран:
 * остался ли на нём слой.
 *
 * Набор текста – отдельный случай: пока курсор в композере, Escape отпускает поле (и заодно
 * закрывает клавиатуру планшета), а окно сворачивает уже следующий. Иначе черновик уезжает в
 * пилюлю от нажатия, которым обычно отменяют ввод.
 */
export const resolveEscapeAction = ({
  defaultPrevented,
  composing,
  layerOpen,
  inTextField,
}: {
  defaultPrevented: boolean;
  /** Ввод иероглифов: Escape отменяет набор в IME, и окно тут ни при чём. */
  composing: boolean;
  layerOpen: boolean;
  inTextField: boolean;
}): EscapeAction => {
  if (defaultPrevented || composing || layerOpen) {
    return 'ignore';
  }

  return inTextField ? 'leaveField' : 'collapse';
};

/**
 * Шаг часов комнаты по фазам: до старта тикают секунды в чипе «через 09:59», в идущей сессии
 * меняются только минуты остатка и ширина прогресса.
 *
 * Раз в секунду перерисовывать окно с лентой сообщений незачем – и заметно на батарее ноутбука,
 * у которого комната висит поверх всех страниц весь вечер.
 */
const CLOCK_STEP_MS: Partial<Record<SessionRoomPhase, number>> = {
  upcoming: 1_000,
  live: 30_000,
};

/**
 * Часы поверхностей комнаты. Возвращают «сейчас» и перерисовывают вызывающего ровно с тем шагом,
 * который его цифрам нужен; в закрытых фазах не тикают вовсе – там меняться уже нечему.
 */
export const useRoomClock = (phase: SessionRoomPhase): number => {
  const [now, setNow] = useState(() => serverNow());
  const step = CLOCK_STEP_MS[phase];

  useEffect(() => {
    if (!step) {
      return undefined;
    }

    // Сверяемся сразу: фаза только что сменилась, и показанное число осталось от прошлой.
    setNow(serverNow());
    const timer = setInterval(() => setNow(serverNow()), step);

    return () => clearInterval(timer);
  }, [step]);

  return now;
};
